'use es6';

import { Component } from 'react';

//#region Imports
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { List, Map as ImmutableMap } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UIFlex from 'UIComponents/layout/UIFlex';
import PropertyFormHeader from 'MeetingsLib/components/header/PropertyFormHeader';
import BookingFormFields from 'MeetingsLib/components/input/BookingFormFields';
import StepButtonsFooter from 'MeetingsLib/components/page/StepButtonsFooter';
import { canSubmitStagedPropertyForm as canSubmitStagedPropertyFormSelector } from 'MeetingsLib/selectors/AutoSubmitSelector';
import { getCustomParams as getCustomParamsSelector, getEvent as getEventSelector, shouldCenterPropertyForm as shouldCenterPropertyFormSelector, getOffline as getOfflineSelector, getIsLegitimateInterest, getPaidMeetingsAvailableSelector, getRecaptchaEnabled } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getStagedFormFieldValidation } from 'MeetingsLib/selectors/FormFieldValidationSelector';
import { getIsFetchingPublicAvailability } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { getStagedLegalConsentResponses, getAllStagedRequiredConsentCollected } from 'MeetingsLib/selectors/LegalConsentFormSelectors';
import FormState from 'MeetingsLib/models/FormState';
import Branding from 'MeetingsBase/models/Branding';
import * as FormActions from 'MeetingsLib/redux/actions/FormActions';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import LegalConsentForm from './LegalConsentForm';
import RecaptchaField from 'MeetingsLib/components/input/RecaptchaField';
//#endregion Imports
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FORM_TOOLTIP_MESSAGES = {
  INCOMPLETE: 'buttons.tooltip.formIncompleteOrHasErrors',
  NO_CONSENT: 'buttons.tooltip.missingConsent'
};
class PropertyForm extends Component {
  constructor(...args) {
    super(...args);
    this.shouldCenterForm = () => {
      const {
        compact,
        centerPropertyForm
      } = this.props;
      return !compact && centerPropertyForm;
    };
    this.handleGoToPreviousStep = () => {
      const {
        resetFormState,
        resetConsentResponses,
        goToPreviousStep
      } = this.props;
      resetFormState();
      resetConsentResponses();
      goToPreviousStep();
    };
    this.handleGoToNextStep = () => {
      const {
        stagedFormState,
        stagedLegalConsentResponses,
        setFormState,
        submitConsentResponses
      } = this.props;
      setFormState(stagedFormState);
      submitConsentResponses(stagedLegalConsentResponses);
    };
    this.getTooltipMessage = () => {
      const {
        isRescheduling,
        canSubmitStagedPropertyForm,
        legalConsentEnabled,
        allRequiredConsentCollected,
        isLegitimateInterest
      } = this.props;
      if (!isRescheduling) {
        if (!canSubmitStagedPropertyForm) {
          return FORM_TOOLTIP_MESSAGES.INCOMPLETE;
        }
        if (legalConsentEnabled && !(allRequiredConsentCollected || isLegitimateInterest)) {
          return FORM_TOOLTIP_MESSAGES.NO_CONSENT;
        }
      }
      return null;
    };
    this.renderFormFields = () => {
      const {
        formFields,
        stagedFormState,
        formStateValidation,
        setFormValue,
        isRescheduling
      } = this.props;
      return /*#__PURE__*/_jsx(UIFlex, {
        direction: "column",
        align: "stretch",
        children: /*#__PURE__*/_jsx(BookingFormFields, {
          centerPropertyForm: this.shouldCenterForm(),
          formFields: formFields,
          formState: stagedFormState,
          formStateValidation: formStateValidation,
          setFormValue: setFormValue,
          isLastStep: true,
          disabled: isRescheduling
        })
      }, "fields");
    };
    this.renderFormHeader = () => {
      const {
        userTimezone,
        dateTime,
        location
      } = this.props;
      return /*#__PURE__*/_jsx(PropertyFormHeader, {
        userTimezone: userTimezone,
        dateTime: dateTime,
        location: location,
        handleGoToPreviousStep: this.handleGoToPreviousStep
      });
    };
    this.renderLegalConsent = () => {
      const {
        compact,
        branding,
        dateTime,
        submittingMeeting
      } = this.props;
      return /*#__PURE__*/_jsx(LegalConsentForm, {
        compact: compact,
        branding: branding,
        submittingMeeting: submittingMeeting,
        dateTime: dateTime
      }, `${compact ? 'compact' : 'full'}-legalConsent`);
    };
    this.renderRecaptcha = () => {
      const {
        compact,
        recaptchaEnabled,
        isPaidMeeting
      } = this.props;
      if (recaptchaEnabled && !isPaidMeeting) {
        const className = compact ? 'm-x-auto' : 'p-x-8';
        return /*#__PURE__*/_jsx("div", {
          className: className,
          children: /*#__PURE__*/_jsx(RecaptchaField, {})
        });
      }
      return null;
    };
    this.renderFormButtons = () => {
      const {
        branding,
        compact,
        submittingMeeting,
        offline,
        isPaidMeeting
      } = this.props;
      const tooltipMessage = this.getTooltipMessage();
      return /*#__PURE__*/_jsx(StepButtonsFooter, {
        branding: branding,
        compact: compact,
        submittingMeeting: submittingMeeting,
        canProceed: tooltipMessage === null,
        tooltipTitle: tooltipMessage ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: tooltipMessage
        }) : null,
        goToPreviousStep: this.handleGoToPreviousStep,
        goToNextStep: this.handleGoToNextStep,
        isLastStep: !isPaidMeeting,
        offline: offline
      }, "buttons");
    };
  }
  render() {
    const {
      canSubmitInitialPropertyForm,
      recaptchaEnabled,
      legalConsentEnabled,
      isRescheduling,
      isPaidMeeting
    } = this.props;
    return /*#__PURE__*/_jsxs(UICardWrapper, {
      className: "flex-column min-height-0",
      "data-selenium-test": "booking-form-step",
      children: [/*#__PURE__*/_jsxs(UICardSection, {
        className: "flex-column min-height-0 p-all-0",
        scrollable: true,
        children: [this.renderFormHeader(), canSubmitInitialPropertyForm && !recaptchaEnabled && !legalConsentEnabled && !isPaidMeeting ? /*#__PURE__*/_jsx(UILoadingSpinner, {
          "data-test-id": "property-form-loading-spinner",
          size: "medium",
          grow: true
        }) : this.renderFormFields(), !isRescheduling && legalConsentEnabled && this.renderLegalConsent(), this.renderRecaptcha()]
      }), (!canSubmitInitialPropertyForm || recaptchaEnabled || legalConsentEnabled || isPaidMeeting) && this.renderFormButtons()]
    });
  }
}
PropertyForm.propTypes = {
  // passed
  branding: PropTypes.instanceOf(Branding).isRequired,
  canSubmitInitialPropertyForm: PropTypes.bool.isRequired,
  compact: PropTypes.bool,
  goToPreviousStep: PropTypes.func.isRequired,
  submittingMeeting: PropTypes.bool,
  //loaded
  canSubmitStagedPropertyForm: PropTypes.bool,
  centerPropertyForm: PropTypes.bool,
  dateTime: PropTypes.number.isRequired,
  location: PropTypes.string,
  formFields: PropTypes.instanceOf(List).isRequired,
  stagedFormState: PropTypes.instanceOf(FormState),
  formStateValidation: PropTypes.instanceOf(ImmutableMap).isRequired,
  recaptchaEnabled: PropTypes.bool.isRequired,
  legalConsentEnabled: PropTypes.bool.isRequired,
  offline: PropTypes.bool,
  setFormValue: PropTypes.func.isRequired,
  setFormState: PropTypes.func.isRequired,
  resetFormState: PropTypes.func.isRequired,
  userTimezone: PropTypes.string.isRequired,
  isRescheduling: PropTypes.bool,
  // data privacy props
  resetConsentResponses: PropTypes.func.isRequired,
  submitConsentResponses: PropTypes.func.isRequired,
  stagedLegalConsentResponses: PropTypes.instanceOf(List),
  allRequiredConsentCollected: PropTypes.bool.isRequired,
  isLegitimateInterest: PropTypes.bool.isRequired,
  isPaidMeeting: PropTypes.bool
};
export default connect(state => ({
  canSubmitStagedPropertyForm: canSubmitStagedPropertyFormSelector(state),
  centerPropertyForm: shouldCenterPropertyFormSelector(state),
  dateTime: getEventSelector(state).dateTime,
  location: getCustomParamsSelector(state).location,
  formFields: getCustomParamsSelector(state).formFields,
  stagedFormState: state.stagedFormState,
  formStateValidation: getStagedFormFieldValidation(state),
  isFetchingPublicAvailability: getIsFetchingPublicAvailability(state),
  recaptchaEnabled: getRecaptchaEnabled(state),
  legalConsentEnabled: getCustomParamsSelector(state).legalConsentEnabled,
  offline: getOfflineSelector(state),
  userTimezone: state.userTimezone,
  isRescheduling: state.isRescheduling,
  stagedLegalConsentResponses: getStagedLegalConsentResponses(state),
  allRequiredConsentCollected: getAllStagedRequiredConsentCollected(state),
  isLegitimateInterest: getIsLegitimateInterest(state),
  isPaidMeeting: getPaidMeetingsAvailableSelector(state)
}), Object.assign({}, FormActions))(PropertyForm);